// 190 -- Coach approval queue. Lists parent self-serve sport signups (188)
// awaiting this coach's approval. One-tap Approve (picking one of the coach's
// teams in that sport) / Decline. Self-hides when empty. Mirrors the 138
// availability-requests card pattern; rendered on the coach dashboard.

import { useEffect, useState } from "react";
import { ClipboardCheck, Check, X, AlertTriangle } from "lucide-react";
import type { CoachEnrollmentRequestPublic } from "@dugads/shared";
import * as api from "@/lib/api";

export function PendingSignupsCard() {
  const [requests, setRequests] = useState<CoachEnrollmentRequestPublic[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Which request is showing its team picker, and the chosen team per request.
  const [pickingId, setPickingId] = useState<string | null>(null);
  const [teamChoice, setTeamChoice] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    api
      .getCoachEnrollmentRequests()
      .then((res) => { if (!cancelled) setRequests(res.requests); })
      .catch(() => { if (!cancelled) setRequests([]); });
    return () => { cancelled = true; };
  }, []);

  if (requests === null || requests.length === 0) return null;

  function drop(id: string) {
    setRequests((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
  }

  async function approve(req: CoachEnrollmentRequestPublic, teamId: string) {
    setBusyId(req.id);
    try {
      await api.approveEnrollmentRequest(req.id, { team_id: teamId });
      drop(req.id);
    } catch {
      /* leave the row so the coach can retry */
    } finally {
      setBusyId(null);
      setPickingId(null);
    }
  }

  function onApproveClick(req: CoachEnrollmentRequestPublic) {
    if (req.eligible_teams.length === 0) return; // no team to place into
    if (req.eligible_teams.length === 1) {
      void approve(req, req.eligible_teams[0].id);
      return;
    }
    setPickingId(pickingId === req.id ? null : req.id);
  }

  async function decline(req: CoachEnrollmentRequestPublic) {
    const reason = window.prompt(`Decline ${req.player_name}'s ${req.sport_name} signup? Optional reason:`);
    if (reason === null) return; // cancelled
    setBusyId(req.id);
    try {
      await api.declineEnrollmentRequest(req.id, { reason: reason.trim() || undefined });
      drop(req.id);
    } catch {
      /* retry */
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-border p-4" data-region="pending-signups">
      <h2 className="flex items-center gap-2 text-lg font-medium">
        <ClipboardCheck className="h-5 w-5 text-primary" aria-hidden /> New signups to approve ({requests.length})
      </h2>
      <ul className="flex flex-col gap-2">
        {requests.map((req) => (
          <li
            key={req.id}
            className="flex flex-col gap-2 rounded-md border border-border bg-card px-3 py-2.5"
            data-item="signup"
            data-item-id={req.id}
          >
            <div>
              <p className="text-sm font-medium text-foreground">
                {req.player_name} &middot; {req.sport_name}
              </p>
              <p className="text-xs text-muted-foreground">
                {req.school_name}
                {req.created_by_name ? ` · from ${req.created_by_name}` : ""} · DOB {req.date_of_birth}
              </p>
              {!req.indemnity_complete && (
                <p className="mt-1 inline-flex items-center gap-1 text-xs text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Indemnity outstanding
                </p>
              )}
            </div>

            {pickingId === req.id && req.eligible_teams.length > 1 && (
              <select
                className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                value={teamChoice[req.id] ?? ""}
                onChange={(e) => setTeamChoice((prev) => ({ ...prev, [req.id]: e.target.value }))}
                data-field="team-pick"
              >
                <option value="">Choose a team...</option>
                {req.eligible_teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}

            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              {pickingId === req.id && req.eligible_teams.length > 1 ? (
                <button
                  type="button"
                  data-action="confirm-approve"
                  disabled={busyId === req.id || !teamChoice[req.id]}
                  onClick={() => approve(req, teamChoice[req.id])}
                  className="inline-flex items-center justify-center gap-1 rounded-md border border-input bg-[#085041] px-3 py-1.5 text-sm font-medium text-white transition disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" aria-hidden /> Confirm
                </button>
              ) : (
                <button
                  type="button"
                  data-action="approve"
                  disabled={busyId === req.id || req.eligible_teams.length === 0}
                  title={req.eligible_teams.length === 0 ? "You have no team in this sport to place them into" : undefined}
                  onClick={() => onApproveClick(req)}
                  className="inline-flex items-center justify-center gap-1 rounded-md border border-input px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" aria-hidden /> Approve
                </button>
              )}
              <button
                type="button"
                data-action="decline"
                disabled={busyId === req.id}
                onClick={() => decline(req)}
                className="inline-flex items-center justify-center gap-1 rounded-md border border-input px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" aria-hidden /> Decline
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
